import { styled } from '@mui/material/styles';
import type { PanelPosition } from '../types/LiveKitVideoDisplay.types';

// Create events for syncing state back to debug display
export const LIVEKIT_VIDEO_STATE_CHANGED = 'livekit-video-state-changed';
export const LIVEKIT_AUDIO_STATE_CHANGED = 'livekit-audio-state-changed';
export const LIVEKIT_SCREEN_SHARE_STATE_CHANGED = 'livekit-screen-share-state-changed';

// Create events for toggling audio/video/screen share
export const LIVEKIT_TOGGLE_AUDIO_EVENT = 'livekit-toggle-audio';
export const LIVEKIT_TOGGLE_VIDEO_EVENT = 'livekit-toggle-video';
export const LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT = 'livekit-toggle-screen-share';

// Create a new event for proximity status changes
export const PLAYER_PROXIMITY_CHANGED = 'player-proximity-changed';

// Sidebar tile layout constants (shared with promotion logic in controller)
export const SIDEBAR_TILE_HEIGHT = 120;
export const SIDEBAR_TILE_WIDTH = 160;
export const SIDEBAR_TILE_GAP = 10;

// Create a constant with the zIndexLayers values we need
export const Z_INDEX = {
  videoOverlay: 15,
  modal: 2000,
  // Screen sharing video z-index hierarchy (highest priority)
  screenSharingVideo: {
    local: {
      normal: 980,
      expanded: 990,
      hover: 1000,
      speaking: 1010,
    },
    remote: {
      normal: 975,
      expanded: 985,
      hover: 995,
      speaking: 1005,
    },
  },
  // Local video z-index hierarchy
  localVideo: {
    normal: 940,
    expanded: 950,
    hover: 960,
    speaking: 970,
  },
  // Remote video z-index hierarchy
  remoteVideo: {
    normal: 900,
    expanded: 910,
    hover: 920,
    speaking: 925,
  },
  // Sidebar video z-index hierarchy
  sidebarVideo: {
    remote: 1,
    remoteHover: 5,
    remoteSpeaking: 10,
    local: 15,
    localHover: 20,
    localSpeaking: 25,
  },
};

// Styled components for video display
export const VideoContainer = styled('div')({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  top: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  zIndex: 900,
});

// Control buttons styling
export const ControlButtonsContainer = styled('div')({
  position: 'fixed',
  bottom: '20px',
  left: '20px',
  display: 'flex',
  gap: '10px',
  zIndex: 900,
});

export const ControlButton = styled('button')({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  '&.active': {
    backgroundColor: '#2196F3',
  },
  '&.muted': {
    backgroundColor: '#f44336',
  },
});

// Display mode toggle button
export const DisplayModeButton = styled(ControlButton)({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
});

// Define VideoWrapper as a regular styled component again
export const VideoWrapper = styled('div')({
  position: 'relative',
  borderRadius: '10px',
  overflow: 'hidden',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    zIndex: 10,
    transform: 'scale(1.02)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
});

// Add speaking styles to the regular VideoWrapper
export const VideoWrapperWithSpeaking = styled(VideoWrapper)({
  '&.speaking': {
    border: '3px solid rgba(0, 255, 0, 0.9) !important',
    boxShadow: '0 0 25px rgba(0, 255, 0, 0.6) !important',
    animation: 'pulse-green 1.2s ease-in-out infinite alternate !important',
    zIndex: '920 !important', // Higher z-index when speaking
    transform: 'scale(1.02) !important',
  },
  '@keyframes pulse-green': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 0.9)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
      transform: 'scale(1.02)',
    },
    '100%': {
      borderColor: 'rgba(50, 255, 50, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.8)',
      transform: 'scale(1.05)',
    },
  },
});

export const LocalVideoWrapper = styled(VideoWrapper)({
  border: '2px solid rgba(0, 200, 0, 0.5)',
  '&.speaking': {
    border: '3px solid rgba(0, 255, 0, 0.9) !important',
    boxShadow: '0 0 25px rgba(0, 255, 0, 0.6) !important',
    animation: 'pulse-green 1.2s ease-in-out infinite alternate !important',
    transform: 'scale(1.02) !important',
  },
  '@keyframes pulse-green': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 0.9)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
      transform: 'scale(1.02)',
    },
    '100%': {
      borderColor: 'rgba(50, 255, 50, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.8)',
      transform: 'scale(1.05)',
    },
  },
});

export const ParticipantName = styled('div')({
  position: 'absolute',
  top: '0px',
  left: '0px',
  right: '0px',
  background: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  padding: '3px 8px',
  borderRadius: '6px 6px 0 0',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  zIndex: 2,
  backdropFilter: 'blur(2px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
});

// Wrapper for the VideoTrack to ensure proper positioning
export const StyledVideoTrack = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  zIndex: 1,
});

// Sidebar components
export const SidebarContainer = styled('div')<{ panelPosition: PanelPosition }>(
  ({ panelPosition }) => ({
    position: 'fixed',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    zIndex: 900,
    ...(panelPosition === 'right' && {
      top: '20px',
      right: '20px',
      bottom: '100px',
      width: '220px',
      flexDirection: 'column' as const,
    }),
    ...(panelPosition === 'left' && {
      top: '20px',
      left: '20px',
      bottom: '100px',
      width: '220px',
      flexDirection: 'column' as const,
    }),
    ...(panelPosition === 'top' && {
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      height: '140px',
      maxWidth: 'calc(100vw - 40px)',
      flexDirection: 'row' as const,
    }),
  })
);

export const SidebarScrollArea = styled('div')<{ panelPosition: PanelPosition }>(
  ({ panelPosition }) => ({
    flex: 1,
    padding: '8px',
    display: 'flex',
    gap: `${SIDEBAR_TILE_GAP}px`,
    /* Hide scrollbar for Webkit browsers */
    '::-webkit-scrollbar': {
      display: 'none',
    },
    /* Hide scrollbar for Firefox */
    scrollbarWidth: 'none' as const,
    /* Hide scrollbar for IE, Edge */
    msOverflowStyle: 'none' as const,
    ...(panelPosition !== 'top'
      ? {
          flexDirection: 'column' as const,
          overflowY: 'auto' as const,
          overflowX: 'hidden' as const,
          contentVisibility: 'auto' as const,
          containIntrinsicSize: '0 400px',
          maxHeight: 'calc(100vh - 140px)',
          minHeight: 0,
        }
      : {
          flexDirection: 'row' as const,
          overflowX: 'auto' as const,
          overflowY: 'hidden' as const,
          maxWidth: '100%',
          minWidth: 0,
        }),
  })
);

export const SidebarVideoWrapper = styled('div')<{ panelPosition?: PanelPosition }>(
  ({ panelPosition }) => ({
  ...(panelPosition === 'top'
    ? {
        width: `${SIDEBAR_TILE_WIDTH}px`,
        minWidth: `${SIDEBAR_TILE_WIDTH}px`,
        maxWidth: `${SIDEBAR_TILE_WIDTH}px`,
        height: '100%',
      }
    : {
        width: '100%',
        height: `${SIDEBAR_TILE_HEIGHT}px`,
        minHeight: `${SIDEBAR_TILE_HEIGHT}px`,
        maxHeight: `${SIDEBAR_TILE_HEIGHT}px`,
      }),
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  flexShrink: 0, // Prevent shrinking
  flexGrow: 0, // Prevent growing
  zIndex: 1, // Lower z-index for remote videos
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    zIndex: 5,
  },
  '&.speaking': {
    border: '3px solid rgba(0, 255, 0, 0.9) !important',
    boxShadow: '0 0 25px rgba(0, 255, 0, 0.6) !important',
    animation: 'pulse-green-sidebar 1.2s ease-in-out infinite alternate !important',
    zIndex: '10 !important', // Higher z-index when speaking
    transform: 'scale(1.05) !important',
  },
  // Screen sharing styles for remote sidebar video
  '&.screen-sharing': {
    border: '3px solid rgba(33, 150, 243, 0.9) !important',
    boxShadow: '0 0 25px rgba(33, 150, 243, 0.6) !important',
    animation: 'pulse-blue-sidebar 1.2s ease-in-out infinite alternate !important',
    zIndex: '30 !important', // Higher z-index when screen sharing
    transform: 'scale(1.05) !important',
  },
  '@keyframes pulse-green-sidebar': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 0.9)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
      transform: 'scale(1.05)',
    },
    '100%': {
      borderColor: 'rgba(50, 255, 50, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.8)',
      transform: 'scale(1.08)',
    },
  },
  '@keyframes pulse-blue-sidebar': {
    '0%': {
      borderColor: 'rgba(33, 150, 243, 0.9)',
      boxShadow: '0 0 25px rgba(33, 150, 243, 0.6)',
      transform: 'scale(1.05)',
    },
    '100%': {
      borderColor: 'rgba(33, 150, 243, 1)',
      boxShadow: '0 0 35px rgba(33, 150, 243, 0.8)',
      transform: 'scale(1.08)',
    },
  },
}));

export const LocalSidebarVideoWrapper = styled(SidebarVideoWrapper)({
  border: '2px solid rgba(0, 200, 0, 0.5)',
  zIndex: 15, // Higher z-index for local video in sidebar
  '&:hover': {
    zIndex: 20, // Even higher on hover
  },
  '&.speaking': {
    border: '3px solid rgba(0, 255, 0, 1) !important',
    boxShadow: '0 0 25px rgba(0, 255, 0, 0.7) !important',
    animation: 'pulse-green-local-sidebar 1.2s ease-in-out infinite alternate !important',
    transform: 'scale(1.05) !important',
    zIndex: '25 !important', // Highest z-index when speaking
  },
  // Screen sharing styles for local sidebar video
  '&.screen-sharing': {
    border: '3px solid rgba(33, 150, 243, 0.9) !important',
    boxShadow: '0 0 25px rgba(33, 150, 243, 0.6) !important',
    animation: 'pulse-blue-local-sidebar 1.2s ease-in-out infinite alternate !important',
    transform: 'scale(1.05) !important',
    zIndex: '35 !important', // Highest z-index when screen sharing
  },
  '@keyframes pulse-blue-local-sidebar': {
    '0%': {
      borderColor: 'rgba(33, 150, 243, 0.9)',
      boxShadow: '0 0 25px rgba(33, 150, 243, 0.6)',
      transform: 'scale(1.05)',
    },
    '100%': {
      borderColor: 'rgba(33, 150, 243, 1)',
      boxShadow: '0 0 35px rgba(33, 150, 243, 0.8)',
      transform: 'scale(1.08)',
    },
  },
  '@keyframes pulse-green-local-sidebar': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.7)',
      transform: 'scale(1.05)',
    },
    '100%': {
      borderColor: 'rgba(50, 255, 50, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.9)',
      transform: 'scale(1.08)',
    },
  },
});

// Screen share related styled components
export const ScreenShareOverlay = styled('div')({
  position: 'absolute',
  top: '8px',
  right: '8px',
  width: '60px',
  height: '45px',
  borderRadius: '4px',
  overflow: 'hidden',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  border: '2px solid rgba(33, 150, 243, 0.8)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  zIndex: 15,
  animation: 'screenSharePopup 0.5s ease-out',
  '&:hover': {
    transform: 'scale(1.1)',
    border: '2px solid rgba(33, 150, 243, 1)',
    boxShadow: '0 0 10px rgba(33, 150, 243, 0.5)',
  },
  '@keyframes screenSharePopup': {
    '0%': {
      transform: 'scale(0) rotate(45deg)',
      opacity: 0,
    },
    '50%': {
      transform: 'scale(1.2) rotate(10deg)',
      opacity: 0.8,
    },
    '100%': {
      transform: 'scale(1) rotate(0deg)',
      opacity: 1,
    },
  },
});

export const ScreenShareLabel = styled('div')({
  position: 'absolute',
  bottom: '0',
  left: '0',
  right: '0',
  background: 'rgba(33, 150, 243, 0.9)',
  color: 'white',
  fontSize: '8px',
  fontWeight: 600,
  textAlign: 'center',
  padding: '1px 2px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

// Screen share preview icon on videos
export const ScreenSharePreview = styled('div')({
  position: 'absolute',
  left: '5px',
  top: '25px',
  width: '22px',
  height: '22px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 10,
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: 'rgba(33, 150, 243, 0.7)',
  },
});

// Create a sidebar-specific participant name component
export const SidebarParticipantName = styled('div')({
  position: 'absolute',
  bottom: '0px',
  left: '0px',
  right: '0px',
  background: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  padding: '3px 8px',
  borderRadius: '0 0 6px 6px',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  zIndex: 2,
  backdropFilter: 'blur(2px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
});

// Create a dynamic video wrapper with size transitions
export const DynamicVideoWrapper = styled('div')<{ isExpanded: boolean; isLocal?: boolean; isScreenSharing?: boolean }>(({ isExpanded, isLocal, isScreenSharing }) => ({
  position: 'fixed',
  left: '0',
  top: '35px',
  width: isExpanded ? '160px' : '80px', // 2x smaller when not expanded
  height: isExpanded ? '120px' : '60px', // 2x smaller when not expanded
  borderRadius: isExpanded ? '8px' : '6px',
  overflow: 'hidden',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  boxShadow: isExpanded ? '0 4px 6px rgba(0, 0, 0, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.15)',
  // Z-index hierarchy: Screen sharing > Local > Remote
  zIndex: isScreenSharing 
    ? (isLocal ? (isExpanded ? 990 : 980) : (isExpanded ? 985 : 975)) // Screen sharing gets highest priority
    : (isLocal ? (isExpanded ? 950 : 940) : (isExpanded ? 910 : 900)), // Normal hierarchy
  willChange: 'transform, width, height',
  contain: 'layout style paint', // CSS containment for better performance
  transform: 'translate(var(--x, 0px), var(--y, 0px)) translate(-50%, -100%)',
  scale: '1',
  // Size/visual transitions only - no transform transition (position updates every frame via imperative style)
  transition:
    'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.3s cubic-bezier(0.4, 0, 0.2, 1), scale 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: isExpanded
      ? '0 8px 25px rgba(0, 0, 0, 0.25)'
      : '0 4px 12px rgba(0, 0, 0, 0.2)',
    zIndex: isScreenSharing 
      ? (isLocal ? 1000 : 995) // Screen sharing hover gets highest priority
      : (isLocal ? 960 : 920), // Normal hover hierarchy
  },
  '&.speaking': {
    border: isExpanded
      ? '3px solid rgba(0, 255, 0, 0.9) !important'
      : '2px solid rgba(0, 255, 0, 1) !important',
    boxShadow: isExpanded
      ? '0 0 25px rgba(0, 255, 0, 0.6) !important'
      : '0 0 15px rgba(0, 255, 0, 0.7) !important',
    animation: isExpanded
      ? 'pulse-green-large 1.2s ease-in-out infinite alternate !important'
      : 'pulse-green-small 1.2s ease-in-out infinite alternate !important',
    zIndex: isScreenSharing 
      ? (isLocal ? '1010 !important' : '1005 !important') // Screen sharing + speaking gets highest priority
      : (isLocal ? '970 !important' : '925 !important'), // Normal speaking hierarchy
    scale: isExpanded ? '1 !important' : '1.1 !important',
  },
  '@keyframes pulse-green-large': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 0.9)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
    },
    '100%': {
      borderColor: 'rgba(50, 255, 50, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.8)',
    },
  },
  '@keyframes pulse-green-small': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 15px rgba(0, 255, 0, 0.7)',
    },
    '100%': {
      borderColor: 'rgba(50, 255, 50, 1)',
      boxShadow: '0 0 20px rgba(0, 255, 0, 0.9)',
    },
  },
  // Adjust participant name styling
  '& .participant-name': {
    fontSize: isExpanded ? '11px' : '8px',
    padding: isExpanded ? '3px 8px' : '2px 4px',
    borderRadius: isExpanded ? '6px 6px 0 0' : '4px 4px 0 0',
  },
  // Scale screen share overlay appropriately
  '& .screen-share-overlay': {
    width: isExpanded ? '60px' : '30px',
    height: isExpanded ? '45px' : '22px',
    top: isExpanded ? '8px' : '4px',
    right: isExpanded ? '8px' : '4px',
  },
  '& .screen-share-label': {
    fontSize: isExpanded ? '8px' : '6px',
    padding: isExpanded ? '1px 2px' : '0.5px 1px',
  },
})); 